import { ProtocolError } from './errors';
import { PROTOCOL_VERSION } from './version';
import type { Direction, PhysicalChannel } from './index';

const HANDSHAKE_MAGIC = 0x53424853;
const OFFER_TYPE = 1;
const RESPONSE_TYPE = 2;
const CONFIRMATION_TYPE = 3;
export const SESSION_DISCOVERY_OFFER_BYTES = 141;
export const SESSION_KEY_RESPONSE_BYTES = 141;
const CONFIRMATION_CONTEXT_BYTES = 64;
export const SESSION_KEY_CONFIRMATION_TAG_BYTES = 32;
export const SESSION_KEY_CONFIRMATION_BYTES =
  CONFIRMATION_CONTEXT_BYTES + SESSION_KEY_CONFIRMATION_TAG_BYTES;
const P256_PUBLIC_KEY_BYTES = 65;
const HASH_BYTES = 32;

function concatBytes(...parts: readonly Uint8Array[]): Uint8Array {
  const output = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

function hexToBytes(value: string): Uint8Array {
  return Uint8Array.from(value.match(/.{2}/gu) ?? [], (byte) => Number.parseInt(byte, 16));
}

const CHANNEL_CODE: Readonly<Record<PhysicalChannel, number>> = {
  optical: 1,
  acoustic: 2,
};

const DIRECTION_CODE: Readonly<Record<Direction, number>> = {
  'sender-to-receiver': 1,
  'receiver-to-sender': 2,
};

export interface SenderDiscoveryOffer {
  readonly kind: 'sender-discovery-offer';
  readonly sessionId: string;
  readonly transferId: string;
  readonly discoveryCommitment: string;
  readonly sequence: number;
  readonly channels: readonly PhysicalChannel[];
  readonly senderPublicKey: Uint8Array;
}

export interface ReceiverKeyResponse {
  readonly kind: 'receiver-key-response';
  readonly sessionId: string;
  readonly transferId: string;
  readonly offerHash: string;
  readonly sequence: number;
  readonly channels: readonly PhysicalChannel[];
  readonly receiverPublicKey: Uint8Array;
}

export interface KeyConfirmationContext {
  readonly kind: 'key-confirmation';
  readonly sessionId: string;
  readonly transcriptHash: string;
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence: number;
}

export interface KeyConfirmationMessage extends KeyConfirmationContext {
  readonly confirmationTag: Uint8Array;
}

export type SequenceReplayFailure = 'replay' | 'too-old';

export class SequenceReplayError extends ProtocolError {
  public readonly failure: SequenceReplayFailure;

  public constructor(failure: SequenceReplayFailure, message: string) {
    super('REPLAY_SEQUENCE', message);
    this.name = 'SequenceReplayError';
    this.failure = failure;
  }
}

function validateUint32(value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new ProtocolError(
      'HANDSHAKE_FIELD_BOUNDS',
      `${field} must be an unsigned 32-bit integer.`,
    );
  }
}

function canonicalHex(value: string, byteLength: number, field: string): Uint8Array {
  const expectedCharacters = byteLength * 2;
  if (value.length !== expectedCharacters || !/^[\da-f]+$/u.test(value)) {
    throw new ProtocolError(
      'HANDSHAKE_HEX_FIELD',
      `${field} must contain exactly ${expectedCharacters} lowercase hexadecimal characters.`,
    );
  }
  return hexToBytes(value);
}

function validatePublicKey(publicKey: Uint8Array, field: string): Uint8Array {
  if (publicKey.length !== P256_PUBLIC_KEY_BYTES || publicKey[0] !== 0x04) {
    throw new ProtocolError(
      'HANDSHAKE_PUBLIC_KEY',
      `${field} must be a 65-byte uncompressed P-256 public key.`,
    );
  }
  return publicKey.slice();
}

export function canonicalPhysicalChannels(
  channels: readonly PhysicalChannel[],
): readonly PhysicalChannel[] {
  const unique = new Set(channels);
  if (unique.size !== channels.length || unique.size < 1) {
    throw new ProtocolError(
      'HANDSHAKE_CHANNELS',
      'Handshake channel lists must contain one or two unique physical channels.',
    );
  }
  for (const channel of unique) {
    if (channel !== 'optical' && channel !== 'acoustic') {
      throw new ProtocolError(
        'HANDSHAKE_CHANNELS',
        'Handshake channel list contains an unknown channel.',
      );
    }
  }
  return (['optical', 'acoustic'] as const).filter((channel) => unique.has(channel));
}

function channelMask(channels: readonly PhysicalChannel[]): number {
  return canonicalPhysicalChannels(channels).reduce(
    (mask, channel) => mask | CHANNEL_CODE[channel],
    0,
  );
}

function channelsFromMask(mask: number): readonly PhysicalChannel[] {
  if (mask < 1 || (mask & ~0x03) !== 0) {
    throw new ProtocolError('HANDSHAKE_CHANNELS', 'Handshake message has an invalid channel mask.');
  }
  return (['optical', 'acoustic'] as const).filter(
    (channel) => (mask & CHANNEL_CODE[channel]) !== 0,
  );
}

function initialiseHeader(type: number, byteLength: number): { bytes: Uint8Array; view: DataView } {
  const bytes = new Uint8Array(byteLength);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, HANDSHAKE_MAGIC);
  view.setUint8(4, PROTOCOL_VERSION.major);
  view.setUint8(5, PROTOCOL_VERSION.minor);
  view.setUint8(6, type);
  return { bytes, view };
}

function decodeHeader(bytes: Uint8Array, type: number, expectedLength: number): DataView {
  if (bytes.length !== expectedLength) {
    throw new ProtocolError(
      'HANDSHAKE_LENGTH',
      'Handshake message length does not match its canonical form.',
    );
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0) !== HANDSHAKE_MAGIC) {
    throw new ProtocolError('HANDSHAKE_MAGIC', 'Handshake message magic does not match SpectraBus.');
  }
  if (
    view.getUint8(4) !== PROTOCOL_VERSION.major ||
    view.getUint8(5) !== PROTOCOL_VERSION.minor
  ) {
    throw new ProtocolError(
      'HANDSHAKE_VERSION',
      'Handshake message uses an unsupported protocol version.',
    );
  }
  if (view.getUint8(6) !== type) {
    throw new ProtocolError('HANDSHAKE_TYPE', 'Handshake message has an unexpected type.');
  }
  return view;
}

export function encodeSenderDiscoveryOffer(offer: SenderDiscoveryOffer): Uint8Array {
  if (offer.kind !== 'sender-discovery-offer') {
    throw new ProtocolError('HANDSHAKE_TYPE', 'Sender discovery offer has an invalid discriminant.');
  }
  validateUint32(offer.sequence, 'Sender offer sequence');
  const sessionId = canonicalHex(offer.sessionId, 16, 'Session identifier');
  const transferId = canonicalHex(offer.transferId, 16, 'Transfer identifier');
  const discoveryCommitment = canonicalHex(
    offer.discoveryCommitment,
    HASH_BYTES,
    'Discovery object commitment',
  );
  const publicKey = validatePublicKey(offer.senderPublicKey, 'Sender public key');
  const { bytes, view } = initialiseHeader(OFFER_TYPE, SESSION_DISCOVERY_OFFER_BYTES);
  view.setUint8(7, channelMask(offer.channels));
  view.setUint32(8, offer.sequence);
  bytes.set(sessionId, 12);
  bytes.set(transferId, 28);
  bytes.set(discoveryCommitment, 44);
  bytes.set(publicKey, 76);
  return bytes;
}

export function decodeSenderDiscoveryOffer(bytes: Uint8Array): SenderDiscoveryOffer {
  const view = decodeHeader(bytes, OFFER_TYPE, SESSION_DISCOVERY_OFFER_BYTES);
  return {
    kind: 'sender-discovery-offer',
    sessionId: bytesToHex(bytes.subarray(12, 28)),
    transferId: bytesToHex(bytes.subarray(28, 44)),
    discoveryCommitment: bytesToHex(bytes.subarray(44, 76)),
    sequence: view.getUint32(8),
    channels: channelsFromMask(view.getUint8(7)),
    senderPublicKey: validatePublicKey(
      bytes.subarray(76, SESSION_DISCOVERY_OFFER_BYTES),
      'Sender public key',
    ),
  };
}

export function encodeReceiverKeyResponse(response: ReceiverKeyResponse): Uint8Array {
  if (response.kind !== 'receiver-key-response') {
    throw new ProtocolError('HANDSHAKE_TYPE', 'Receiver key response has an invalid discriminant.');
  }
  validateUint32(response.sequence, 'Receiver response sequence');
  const sessionId = canonicalHex(response.sessionId, 16, 'Session identifier');
  const transferId = canonicalHex(response.transferId, 16, 'Transfer identifier');
  const offerHash = canonicalHex(response.offerHash, HASH_BYTES, 'Sender offer hash');
  const publicKey = validatePublicKey(response.receiverPublicKey, 'Receiver public key');
  const { bytes, view } = initialiseHeader(RESPONSE_TYPE, SESSION_KEY_RESPONSE_BYTES);
  view.setUint8(7, channelMask(response.channels));
  view.setUint32(8, response.sequence);
  bytes.set(sessionId, 12);
  bytes.set(transferId, 28);
  bytes.set(offerHash, 44);
  bytes.set(publicKey, 76);
  return bytes;
}

export function decodeReceiverKeyResponse(bytes: Uint8Array): ReceiverKeyResponse {
  const view = decodeHeader(bytes, RESPONSE_TYPE, SESSION_KEY_RESPONSE_BYTES);
  return {
    kind: 'receiver-key-response',
    sessionId: bytesToHex(bytes.subarray(12, 28)),
    transferId: bytesToHex(bytes.subarray(28, 44)),
    offerHash: bytesToHex(bytes.subarray(44, 76)),
    sequence: view.getUint32(8),
    channels: channelsFromMask(view.getUint8(7)),
    receiverPublicKey: validatePublicKey(
      bytes.subarray(76, SESSION_KEY_RESPONSE_BYTES),
      'Receiver public key',
    ),
  };
}

export function encodeKeyConfirmationContext(context: KeyConfirmationContext): Uint8Array {
  if (context.kind !== 'key-confirmation') {
    throw new ProtocolError('HANDSHAKE_TYPE', 'Key confirmation has an invalid discriminant.');
  }
  validateUint32(context.sequence, 'Key confirmation sequence');
  if (
    (context.direction !== 'sender-to-receiver' &&
      context.direction !== 'receiver-to-sender') ||
    (context.channel !== 'optical' && context.channel !== 'acoustic')
  ) {
    throw new ProtocolError(
      'HANDSHAKE_BINDING',
      'Key confirmation contains an unknown direction or physical channel.',
    );
  }
  const sessionId = canonicalHex(context.sessionId, 16, 'Session identifier');
  const transcriptHash = canonicalHex(context.transcriptHash, HASH_BYTES, 'Transcript hash');
  const { bytes, view } = initialiseHeader(CONFIRMATION_TYPE, CONFIRMATION_CONTEXT_BYTES);
  view.setUint8(7, DIRECTION_CODE[context.direction]);
  view.setUint8(8, CHANNEL_CODE[context.channel]);
  view.setUint32(12, context.sequence);
  bytes.set(sessionId, 16);
  bytes.set(transcriptHash, 32);
  return bytes;
}

export function encodeKeyConfirmationMessage(message: KeyConfirmationMessage): Uint8Array {
  if (message.confirmationTag.length !== SESSION_KEY_CONFIRMATION_TAG_BYTES) {
    throw new ProtocolError(
      'HANDSHAKE_CONFIRMATION_TAG',
      'Key confirmation tag must contain exactly 256 bits.',
    );
  }
  return concatBytes(encodeKeyConfirmationContext(message), message.confirmationTag);
}

export function decodeKeyConfirmationMessage(bytes: Uint8Array): KeyConfirmationMessage {
  const view = decodeHeader(bytes, CONFIRMATION_TYPE, SESSION_KEY_CONFIRMATION_BYTES);
  if (view.getUint8(9) !== 0 || view.getUint8(10) !== 0 || view.getUint8(11) !== 0) {
    throw new ProtocolError('HANDSHAKE_RESERVED', 'Key confirmation has non-zero reserved bytes.');
  }
  const directionCode = view.getUint8(7);
  const channelCode = view.getUint8(8);
  const direction =
    directionCode === 1
      ? 'sender-to-receiver'
      : directionCode === 2
        ? 'receiver-to-sender'
        : null;
  const channel = channelCode === 1 ? 'optical' : channelCode === 2 ? 'acoustic' : null;
  if (direction === null || channel === null) {
    throw new ProtocolError(
      'HANDSHAKE_BINDING',
      'Key confirmation contains an unknown direction or physical channel.',
    );
  }
  return {
    kind: 'key-confirmation',
    sessionId: bytesToHex(bytes.subarray(16, 32)),
    transcriptHash: bytesToHex(bytes.subarray(32, 64)),
    direction,
    channel,
    sequence: view.getUint32(12),
    confirmationTag: bytes.slice(CONFIRMATION_CONTEXT_BYTES),
  };
}

export class SequenceReplayWindow {
  readonly #windowSize: number;
  readonly #mask: bigint;
  #highWaterMark: number | null = null;
  #seen = 0n;

  public constructor(windowSize = 64) {
    if (!Number.isInteger(windowSize) || windowSize < 1 || windowSize > 256) {
      throw new ProtocolError(
        'REPLAY_WINDOW_BOUNDS',
        'Replay window size must be an integer between 1 and 256 symbols.',
      );
    }
    this.#windowSize = windowSize;
    this.#mask = (1n << BigInt(windowSize)) - 1n;
  }

  public get highWaterMark(): number | null {
    return this.#highWaterMark;
  }

  public accept(sequence: number): void {
    validateUint32(sequence, 'Replay sequence');
    if (this.#highWaterMark === null) {
      this.#highWaterMark = sequence;
      this.#seen = 1n;
      return;
    }

    if (sequence > this.#highWaterMark) {
      const advance = sequence - this.#highWaterMark;
      this.#seen =
        advance >= this.#windowSize
          ? 1n
          : ((this.#seen << BigInt(advance)) | 1n) & this.#mask;
      this.#highWaterMark = sequence;
      return;
    }

    const age = this.#highWaterMark - sequence;
    if (age >= this.#windowSize) {
      throw new SequenceReplayError(
        'too-old',
        'Authenticated sequence falls outside the replay window.',
      );
    }
    const bit = 1n << BigInt(age);
    if ((this.#seen & bit) !== 0n) {
      throw new SequenceReplayError('replay', 'Authenticated sequence has already been accepted.');
    }
    this.#seen |= bit;
  }
}
